import { useEffect, useMemo, useRef, useState } from 'react';
import { addDays, toZoned } from '@shared/time';
import { api, ApiRequestError } from '../../lib/api';
import type { AvailabilityResponse, Slot } from '../types';
import { buildMonth, monthOf, shiftMonth, weekdayLabels } from '../lib/calendar';
import { useI18n } from '../lib/i18n';
import { browserTimeZone, dateInZone, formatLocalDate, formatTime, zoneLabel } from '../lib/format';
import { ChevronIcon } from './Icons';

interface Props {
  slug: string;
  serviceId: string;
  /** Omit or null for "any available provider". */
  providerId?: string | null;
  /** The customer's provider preference (0051); narrows "any available" to eligible people. */
  providerPreference?: string | null;
  /** Offered when the preference is why the day is empty: nobody eligible offers this service. */
  onClearPreference?: (() => void) | null;
  locationId?: string | null;
  /** The location's zone: the calendar day the API works in. */
  locationZone: string;
  maxAdvanceDays: number;
  selected: Slot | null;
  onSelect: (slot: Slot | null) => void;
  /** Bump to discard cached availability and reload the selected day. */
  reloadToken?: number;
  /**
   * The day to open on when no slot is selected. The picker is unmounted
   * while the customer fills in their details, so without this a customer
   * sent back here — because the time went to somebody else — would land
   * on "pick a day" and have to find their day again at the one moment
   * they are least pleased with us.
   */
  initialDate?: string | null;
  /** An instant to exclude (the appointment being rescheduled). */
  excludeStart?: string;
  /**
   * Offered next to an empty day when the business runs a waitlist. Given
   * the day the customer was looking at, so the ask starts from there.
   */
  onAskWaitlist?: ((date: string) => void) | null;
}

type DayState = { status: 'idle' } | { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; slots: Slot[]; noProviderForPreference?: boolean };

const GROUPS: { id: string; key: 'picker.morning' | 'picker.afternoon' | 'picker.evening'; test: (hour: number) => boolean }[] = [
  { id: 'morning', key: 'picker.morning', test: (h) => h < 12 },
  { id: 'afternoon', key: 'picker.afternoon', test: (h) => h >= 12 && h < 17 },
  { id: 'evening', key: 'picker.evening', test: (h) => h >= 17 },
];

export function DateTimePicker(props: Props) {
  const { t, locale } = useI18n();
  const { slug, serviceId, providerId, providerPreference = null, onClearPreference = null, locationId, locationZone, maxAdvanceDays, selected, onSelect, reloadToken = 0, initialDate = null, excludeStart, onAskWaitlist } = props;
  const customerZone = useMemo(browserTimeZone, []);
  const today = useMemo(() => toZoned(locationZone, Date.now()).date, [locationZone]);
  const minDate = today;
  const maxDate = useMemo(() => addDays(today, maxAdvanceDays), [today, maxAdvanceDays]);

  const [selectedDate, setSelectedDate] = useState<string | null>(
    selected ? dateInZone(locationZone, selected.starts_at) : initialDate,
  );
  const [viewMonth, setViewMonth] = useState(monthOf(selectedDate ?? today));
  const [day, setDay] = useState<DayState>({ status: 'idle' });
  const [retry, setRetry] = useState(0);
  const cache = useRef(new Map<string, { slots: Slot[]; noProviderForPreference: boolean }>());

  // A different service/provider/location means different availability.
  useEffect(() => {
    cache.current.clear();
  }, [serviceId, providerId, providerPreference, locationId, reloadToken]);

  useEffect(() => {
    if (!selectedDate) {
      setDay({ status: 'idle' });
      return;
    }
    const key = `${serviceId}|${providerId ?? ''}|${providerPreference ?? ''}|${locationId ?? ''}|${selectedDate}`;
    const cached = cache.current.get(key);
    if (cached) {
      setDay({ status: 'ready', slots: cached.slots, noProviderForPreference: cached.noProviderForPreference });
      return;
    }
    let cancelled = false;
    setDay({ status: 'loading' });
    const q = new URLSearchParams({ service_id: serviceId, date: selectedDate, tz: customerZone });
    if (providerId) q.set('provider_id', providerId);
    if (locationId) q.set('location_id', locationId);
    if (!providerId && providerPreference) q.set('provider_preference', providerPreference);
    api<AvailabilityResponse>(`/public/${encodeURIComponent(slug)}/availability?${q}`)
      .then((res) => {
        if (cancelled) return;
        const slots = dedupe(res.slots ?? [], Boolean(providerId));
        const noProviderForPreference = res.no_provider_for_preference === true;
        cache.current.set(key, { slots, noProviderForPreference });
        setDay({ status: 'ready', slots, noProviderForPreference });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const message = e instanceof ApiRequestError ? e.message : t('picker.times_error');
        setDay({ status: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, serviceId, providerId, providerPreference, locationId, selectedDate, customerZone, reloadToken, retry]);

  const month = useMemo(() => buildMonth(viewMonth, today, minDate, maxDate, locale), [viewMonth, today, minDate, maxDate, locale]);
  const canPrev = viewMonth > monthOf(minDate);
  const canNext = viewMonth < monthOf(maxDate);
  const zonesDiffer = customerZone !== locationZone;

  const grouped = useMemo(() => {
    if (day.status !== 'ready') return [];
    const slots = day.slots.filter((s) => s.starts_at !== excludeStart);
    return GROUPS.map((g) => ({
      ...g,
      slots: slots.filter((s) => g.test(toZoned(customerZone, s.starts_at).hour)),
    })).filter((g) => g.slots.length > 0);
  }, [day, customerZone, excludeStart]);

  function pickDate(date: string) {
    setSelectedDate(date);
    onSelect(null);
  }

  return (
    <div className="picker">
      <div className="calendar" role="group" aria-label={t('picker.choose_day')}>
        <div className="calendar-head">
          <button type="button" className="icon-btn" onClick={() => setViewMonth(shiftMonth(viewMonth, -1))} disabled={!canPrev} aria-label={t('picker.previous_month')}>
            <ChevronIcon dir="left" />
          </button>
          <span className="calendar-title" aria-live="polite">{month.label}</span>
          <button type="button" className="icon-btn" onClick={() => setViewMonth(shiftMonth(viewMonth, 1))} disabled={!canNext} aria-label={t('picker.next_month')}>
            <ChevronIcon dir="right" />
          </button>
        </div>
        <div className="calendar-grid">
          {weekdayLabels(locale).map((w) => (
            <div key={w} className="calendar-weekday" aria-hidden="true">{w}</div>
          ))}
          {month.weeks.flat().map((cell) => {
            const isSelected = cell.date === selectedDate;
            const cls = `calendar-day${cell.inMonth ? '' : ' is-outside'}${isSelected ? ' is-selected' : ''}${cell.isToday ? ' is-today' : ''}`;
            return cell.inMonth ? (
              <button
                key={cell.date}
                type="button"
                className={cls}
                disabled={!cell.selectable}
                aria-pressed={isSelected}
                aria-label={formatLocalDate(cell.date, {}, locale)}
                onClick={() => pickDate(cell.date)}
              >
                {cell.day}
              </button>
            ) : (
              <div key={cell.date} className={cls} aria-hidden="true" />
            );
          })}
        </div>
        <p className="hint">You can book up to {maxAdvanceDays} days ahead.</p>
      </div>

      <div className="times" aria-live="polite" aria-busy={day.status === 'loading'}>
        {!selectedDate && <p className="muted">{t('picker.pick_a_day')}</p>}
        {selectedDate && (
          <>
            <h3 className="times-title">{formatLocalDate(selectedDate, {}, locale)}</h3>
            <p className="zone-note">
              Times shown in <strong>{zoneLabel(customerZone)}</strong> (your time).
              {zonesDiffer && (
                <> The business is in <strong>{zoneLabel(locationZone)}</strong>; their local time is shown under each slot.</>
              )}
            </p>
            {day.status === 'loading' && <p className="muted">Loading times…</p>}
            {day.status === 'error' && (
              <p className="error" role="alert">
                {day.message}{' '}
                <button type="button" className="link-btn" onClick={() => { cache.current.clear(); setRetry((n) => n + 1); }}>
                  Retry
                </button>
              </p>
            )}
            {day.status === 'ready' && grouped.length === 0 && day.noProviderForPreference && (
              // The preference is the reason, and it is said so: the
              // customer chooses to drop it or to look elsewhere. It is
              // never dropped for them.
              <div className="empty" role="status">
                <p>{t('booking.preference_none_available')}</p>
                {onClearPreference && (
                  <button type="button" className="btn btn--primary" onClick={onClearPreference}>{t('booking.preference_choose_none')}</button>
                )}
              </div>
            )}
            {day.status === 'ready' && grouped.length === 0 && !day.noProviderForPreference && (
              <div className="empty">
                <p>{t('picker.no_times_this_day')}</p>
                {providerPreference && providerPreference !== 'no_preference' && onClearPreference && (
                  <p><button type="button" className="link-btn" onClick={onClearPreference}>{t('booking.preference_choose_none')}</button> {t('booking.preference_choose_time')}</p>
                )}
                {onAskWaitlist && selectedDate && (
                  <button type="button" className="link-btn" onClick={() => onAskWaitlist(selectedDate)}>
                    Or tell me when something opens
                  </button>
                )}
              </div>
            )}
            {day.status === 'ready' &&
              grouped.map((g) => (
                <div key={g.id} className="time-group" role="group" aria-label={t(g.key)}>
                  <h4>{t(g.key)}</h4>
                  <div className="time-grid">
                    {g.slots.map((s) => {
                      const active = selected?.starts_at === s.starts_at;
                      const customerDate = dateInZone(customerZone, s.starts_at);
                      const otherDay = customerDate !== selectedDate;
                      return (
                        <button
                          key={s.starts_at}
                          type="button"
                          className={`time-btn${active ? ' is-selected' : ''}`}
                          aria-pressed={active}
                          onClick={() => onSelect(s)}
                        >
                          <span className="time-main">
                            {otherDay && <span className="time-day">{formatLocalDate(customerDate, { weekday: 'short', year: undefined, month: undefined, day: undefined }, locale)} </span>}
                            {formatTime(customerZone, s.starts_at, locale)}
                          </span>
                          {zonesDiffer && <span className="time-alt">{formatTime(locationZone, s.starts_at, locale)} {t('picker.there')}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
}

/** With "any provider", several providers share a start; show it once. */
function dedupe(slots: Slot[], perProvider: boolean): Slot[] {
  const sorted = [...slots].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  if (perProvider) return sorted;
  const seen = new Set<string>();
  return sorted.filter((s) => (seen.has(s.starts_at) ? false : (seen.add(s.starts_at), true)));
}
